// Pure, framework-free state helpers for the "Manage Use Cases" modal.
//
// A single use-case-persona link is a TRIPLE `(useCaseId, personaId, roleValue)`
// where `roleValue` is a `ROLE_OPTIONS.value` (== the stored `deType`). Selection
// state is a plain `Record<tripleKey, true>` map — immutable via spread, never
// mutated — so toggling is O(1) and the add/remove diff is a set difference.
//
// Everything here is deterministic and side-effect free; the modal container
// owns React state and wiring, these helpers own the data transforms so they can
// be unit-tested in isolation.

import { ROLE_OPTIONS, roleLabel } from './dsv-role-options';

// Unit separator (U+001F) — never appears in ids or role values, so it is a safe
// delimiter for composing/decomposing triple keys.
const DELIM = '';

const DEFAULT_PERSONA_ID = 'persona_default';

/**
 * Compose the canonical key for a link triple.
 * @param {string} useCaseId
 * @param {string} personaId
 * @param {string} roleValue
 * @returns {string}
 */
function tripleKey(useCaseId, personaId, roleValue) {
	return `${useCaseId}${DELIM}${personaId}${DELIM}${roleValue}`;
}

/**
 * Decompose a triple key back into its parts.
 * @param {string} key
 * @returns {{ useCaseId: string, personaId: string, roleValue: string }}
 */
function parseTriple(key) {
	const [useCaseId, personaId, roleValue] = String(key).split(DELIM);
	return { useCaseId, personaId, roleValue };
}

/**
 * Snapshot the existing links into the selection map + a triple→link-id map (for
 * mapping removals back to the DataElementPersona rows to delete).
 * @param {Array<object>} deLinks serialized DE-link rows (see serializeDeLink)
 * @returns {{ initialSelected: Record<string, true>, linkIdByTriple: Record<string, string> }}
 */
function seedFromDeLinks(deLinks) {
	const initialSelected = {};
	const linkIdByTriple = {};
	for (const row of deLinks || []) {
		if (!row || !row.useCaseId || !row.personaId || !row.deType) continue;
		const key = tripleKey(row.useCaseId, row.personaId, row.deType);
		initialSelected[key] = true;
		if (row.dataElementPersonaId) linkIdByTriple[key] = row.dataElementPersonaId;
	}
	return { initialSelected, linkIdByTriple };
}

/**
 * Toggle one triple in the working selection, returning a NEW map (never mutates
 * the input).
 * @param {Record<string, true>} edited
 * @param {string} useCaseId
 * @param {string} personaId
 * @param {string} roleValue
 * @returns {Record<string, true>}
 */
function toggleTriple(edited, useCaseId, personaId, roleValue) {
	const key = tripleKey(useCaseId, personaId, roleValue);
	if (edited[key]) {
		const next = { ...edited };
		delete next[key];
		return next;
	}
	return { ...edited, [key]: true };
}

/**
 * Set difference between the seeded snapshot and the working copy.
 * @param {Record<string, true>} initialSelected
 * @param {Record<string, true>} edited
 * @returns {{ additionKeys: string[], removalKeys: string[] }}
 */
function diffSelection(initialSelected, edited) {
	const initial = initialSelected || {};
	const current = edited || {};
	const additionKeys = Object.keys(current).filter((k) => !initial[k]);
	const removalKeys = Object.keys(initial).filter((k) => !current[k]);
	return { additionKeys, removalKeys };
}

/**
 * Map removal triple keys to the DataElementPersona ids to delete. Drops any
 * unmapped key (defensive) and de-dups the result.
 * @param {string[]} removalKeys
 * @param {Record<string, string>} linkIdByTriple
 * @returns {string[]}
 */
function removalIds(removalKeys, linkIdByTriple) {
	const ids = [];
	const seen = new Set();
	for (const key of removalKeys || []) {
		const id = linkIdByTriple ? linkIdByTriple[key] : undefined;
		if (!id || seen.has(id)) continue;
		seen.add(id);
		ids.push(id);
	}
	return ids;
}

/**
 * Build the POST /usecasepersonas body from the staged additions — one entry per
 * use case that gained ≥1 link, with each role key carrying only the personas
 * added under that role (per-persona role granularity).
 * @param {{ additionKeys: string[], useCaseNameById: Record<string,string>, dataElementId: string }} params
 * @returns {Array<object>}
 */
function buildAdditionsPayload({ additionKeys, useCaseNameById, dataElementId }) {
	const byUseCase = new Map();
	for (const key of additionKeys || []) {
		const { useCaseId, personaId, roleValue } = parseTriple(key);
		if (!byUseCase.has(useCaseId)) byUseCase.set(useCaseId, []);
		byUseCase.get(useCaseId).push({ personaId, roleValue });
	}

	const names = useCaseNameById || {};
	const payload = [];
	for (const [useCaseId, triples] of byUseCase.entries()) {
		const personaOrder = [];
		const seenPersonas = new Set();
		for (const { personaId } of triples) {
			if (seenPersonas.has(personaId)) continue;
			seenPersonas.add(personaId);
			personaOrder.push(personaId);
		}

		const dataElement = { id: dataElementId };
		for (const role of ROLE_OPTIONS) {
			dataElement[role.payloadKey] = triples
				.filter((t) => t.roleValue === role.value)
				.map((t) => t.personaId);
		}

		payload.push({
			id: useCaseId,
			name: names[useCaseId] ?? useCaseId,
			// DSV-023: one UseCasePersona upsert per selected persona. Backend keys
			// on (personaId, useCaseId); keyValueDrivers/description are written
			// as-is, so send [] / '' rather than omitting them.
			personas: personaOrder.map((id) => ({ id, keyValueDrivers: [], description: '' })),
			dataElements: [dataElement],
		});
	}
	return payload;
}

/**
 * Build the step-2 view model: the complete resulting state grouped by use case
 * → persona → roles, each role carrying an added/unchanged/removed status.
 * @param {{ initialSelected: Record<string,true>, edited: Record<string,true>, useCaseNameById: Record<string,string>, personaNameById: Record<string,string> }} params
 * @returns {Array<{ useCaseId: string, useCaseName: string, personas: Array<object> }>}
 */
function buildReviewModel({ initialSelected, edited, useCaseNameById, personaNameById }) {
	const initial = initialSelected || {};
	const current = edited || {};
	const ucNames = useCaseNameById || {};
	const personaNames = personaNameById || {};

	const { removalKeys } = diffSelection(initial, current);
	const allKeys = [...Object.keys(current), ...removalKeys];

	const statusOf = (key) => {
		if (current[key]) return initial[key] ? 'unchanged' : 'added';
		return 'removed';
	};

	const useCaseMap = new Map();
	for (const key of allKeys) {
		const { useCaseId, personaId, roleValue } = parseTriple(key);
		if (!useCaseMap.has(useCaseId)) {
			useCaseMap.set(useCaseId, {
				useCaseId,
				useCaseName: ucNames[useCaseId] ?? useCaseId,
				personas: new Map(),
			});
		}
		const group = useCaseMap.get(useCaseId);
		if (!group.personas.has(personaId)) {
			group.personas.set(personaId, {
				personaId,
				personaName: personaNames[personaId] ?? personaId,
				roles: [],
			});
		}
		group.personas.get(personaId).roles.push({
			roleValue,
			roleLabel: roleLabel(roleValue),
			status: statusOf(key),
		});
	}

	return [...useCaseMap.values()].map((group) => ({
		useCaseId: group.useCaseId,
		useCaseName: group.useCaseName,
		personas: [...group.personas.values()],
	}));
}

/**
 * Buildex fast-path: stage `(soleUc, persona_default, PROVIDER)` so the modal
 * opens with a non-empty diff (Review → Confirm = 2 clicks). Only fires for the
 * pristine buildex shape — exactly one use case, `persona_default` a real
 * persona, and zero existing links. Otherwise returns `edited` untouched (FK-safe
 * on tradex, where `persona_default` is not a real Persona).
 * @param {{ edited: Record<string,true>, initialSelected: Record<string,true>, useCaseOptions: Array<{id:string}>, personaOptions: Array<{id:string}> }} params
 * @returns {Record<string, true>}
 */
function applyBuildexDefault({ edited, initialSelected, useCaseOptions, personaOptions }) {
	const ucs = useCaseOptions || [];
	if (ucs.length !== 1) return edited;
	if (Object.keys(initialSelected || {}).length !== 0) return edited;
	const soleUc = ucs[0].id;
	if (!soleUc) return edited;
	const hasDefaultPersona = (personaOptions || []).some((p) => p.id === DEFAULT_PERSONA_ID);
	if (!hasDefaultPersona) return edited;
	return { ...edited, [tripleKey(soleUc, DEFAULT_PERSONA_ID, 'PROVIDER')]: true };
}

/**
 * Which accordion sections should open on modal open: the sole use case when
 * there is exactly one, otherwise every use case that already has links.
 * @param {Array<{id:string}>} useCaseOptions
 * @param {Record<string, true>} initialSelected
 * @returns {Set<string>}
 */
function seedExpanded(useCaseOptions, initialSelected) {
	const ucs = useCaseOptions || [];
	if (ucs.length === 1) return new Set([ucs[0].id]);
	const withLinks = new Set();
	for (const key of Object.keys(initialSelected || {})) {
		withLinks.add(parseTriple(key).useCaseId);
	}
	return withLinks;
}

export {
	DELIM,
	DEFAULT_PERSONA_ID,
	tripleKey,
	parseTriple,
	seedFromDeLinks,
	toggleTriple,
	diffSelection,
	removalIds,
	buildAdditionsPayload,
	buildReviewModel,
	applyBuildexDefault,
	seedExpanded,
};
